<template>
  <div class="view view-auth">
    <div class="view-auth__form">
      <template v-if="form === 'login'">
        <login-form
          v-model:email="email"
          :should-autoload-data="shouldAutoloadData"
          @login="onLogin"
        >
          <template #header>
            <logo class="view-auth__logo" />
          </template>
          <template #forgot-password>
            <router-link :to="{ name: 'reset-password' }">
              {{ $t("labels.forgotPassword") }}
            </router-link>
          </template>
        </login-form>
        <div class="text-center pt-6 mt-3" style="border-top: 1px solid var(--color-border)">
          <router-link :to="{ name: 'register' }">
            {{ $t("labels.register") }}
          </router-link>
        </div>
      </template>

      <template v-else-if="form == 'reset-password'">
        <reset-password-form v-model:email="email">
          <logo />
        </reset-password-form>
        <div class="text-center pt-6 mt-6" style="border-top: 1px solid var(--color-border)">
          <router-link :to="{ name: 'login' }" class="ta-center">
            {{ $t("links.backToLogin") }}
          </router-link>
        </div>
      </template>

      <template v-else-if="form == 'register'">
        <register-form
          v-model:email="email"
          :step="step"
          @update:step="updateStep($event)"
          @register="onLogin()"
        >
          <template #after="{ step }">
            <div class="text-center pt-6 mt-6" v-if="step == 'register'" style="border-top: 1px solid var(--color-border)">
              <router-link :to="{ name: 'login' }">
                {{ $t("links.backToLogin") }}
              </router-link>
            </div>
          </template>
        </register-form>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import LoginForm from "./LoginForm.vue";
import RegisterForm from "./RegisterForm.vue";
import ResetPasswordForm from "./ResetPasswordForm.vue";
import Logo from "@core/ui/SimcisionLogo.vue";
import { useUser } from "@/api/user.store";

withDefaults(
  defineProps<{
    redirect?: string | null;
    form: "login" | "register" | "reset-password";
    step?: "register" | "confirm-email" | "password";
  }>(),
  {
    redirect: null,
    form: "login"
  }
);

const emit = defineEmits<{
  (e: "update:step", step: "register" | "confirm-email" | "password"): void;
}>();

const { logout } = useUser();
const route = useRoute();
const router = useRouter();

const email = ref("");

const shouldAutoloadData = computed(() => {
  return !route.query.logout;
});

const redirectPath = computed(() => {
  if (route.redirectedFrom) {
    return {
      path: route.redirectedFrom.path,
      params: route.redirectedFrom.params
    };
  }
  return {
    name: "models-explorer"
  };
});

if (route.query.logout) {
  logout();
}

function updateStep(step: "register" | "confirm-email" | "password") {
  emit("update:step", step);
  if (step === "confirm-email") {
    router.push({ name: "register-confirm" });
  }
  if (step === "password") {
    router.push({ name: "register-password" });
  }
}
function onLogin() {
  router.push(redirectPath.value);
}
</script>

<style lang="scss">
.view-auth {
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 1rem;

  &__form {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: auto;
    width: 100%;
    max-width: 30rem;

    padding: 3rem;
    background-color: var(--color-background-deep);
    box-shadow: 0 5px 30px var(--color-shadow-1);
    border-radius: 1rem;
    max-height: 100vh;
    overflow: auto;
  }

  &__logo {
    margin: 0 auto;
    flex-shrink: 0;
  }
}
</style>
